import { useEffect, useRef, useState, useMemo } from "react";
import { useInView } from "framer-motion";

// ─── Deterministic deviation profile — raw forecast error per 15-min block (%) ───
const RAW = (() => {
  let s = 7;
  const rnd = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const out = [];
  for (let i = 0; i < 96; i++) {
    let v = (rnd() - 0.5) * 1.5;
    if (rnd() < 0.17) v = (rnd() > 0.5 ? 1 : -1) * (1.3 + rnd() * 2.1);
    out.push(v);
  }
  return out;
})();

const BAND = 1.0;
const clampBand = (v) => Math.max(-0.85, Math.min(0.85, v));

export function DsmGuard() {
  const ref = useRef(null);
  const iv = useInView(ref, { margin: "-15% 0px" });
  const [block, setBlock] = useState(0);

  useEffect(() => {
    if (!iv) return;
    let rafId, last = 0;
    const tick = (ts) => {
      rafId = requestAnimationFrame(tick);
      if (ts - last < 95) return;
      last = ts;
      if (document.hidden) return;
      setBlock(b => (b + 1) % 96);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [iv]);

  const { breaches, exposure } = useMemo(() => {
    let n = 0, rs = 0;
    for (let i = 0; i <= block; i++) {
      if (Math.abs(RAW[i]) > BAND) { n++; rs += Math.round((Math.abs(RAW[i]) - BAND) * 5200 + 1800); }
    }
    return { breaches: n, exposure: rs };
  }, [block]);
  const revisions = Math.min(8, Math.ceil(breaches / 3));

  const hr = Math.floor(block / 4), mn = (block % 4) * 15;
  const time = `${String(hr).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
  const cur = clampBand(RAW[block]);
  const risky = Math.abs(RAW[block]) > BAND;
  const mc = risky ? '#FFB800' : '#CCFF00';

  // SVG geometry — viewBox 100 x 56, zero axis at y=28, 1% = 7 units
  const MID = 28, SCALE = 7;
  const yFor = (v) => MID - v * SCALE;
  const bw = 100 / 96;

  return (
    <div ref={ref} className="dsm" data-testid="dsm-guard">
      {/* ── Header ── */}
      <div className="dsm-hd">
        <div className="dsm-hd-left">
          <span className="dsm-dot" style={{ background: mc, boxShadow: `0 0 8px ${mc}` }} />
          <span className="dsm-hd-title">DSM Guard &middot; CERC DSM Regulations 2024</span>
        </div>
        <div className="dsm-hd-right">
          <span className="dsm-hd-time" style={{ color: mc }}>{time}</span>
          <span className="dsm-hd-dev">{cur >= 0 ? '+' : ''}{cur.toFixed(2)}%</span>
          <span className="dsm-hd-badge" style={risky ? { color: '#FFB800', borderColor: 'rgba(255,184,0,0.3)' } : {}}>
            {risky ? 'REVISION FILED' : 'IN BAND'}
          </span>
        </div>
      </div>

      {/* ── Deviation chart ── */}
      <div className="dsm-chart">
        <div className="dsm-svg-box">
        <svg viewBox="0 0 100 56" preserveAspectRatio="none" className="dsm-svg">
          {/* DSM band */}
          <rect x="0" y={yFor(BAND)} width="100" height={BAND * 2 * SCALE} fill="rgba(204,255,0,0.045)" />
          <line x1="0" y1={yFor(BAND)} x2="100" y2={yFor(BAND)} stroke="#CCFF00" strokeWidth="0.08" strokeDasharray="0.6,0.9" opacity="0.4" />
          <line x1="0" y1={yFor(-BAND)} x2="100" y2={yFor(-BAND)} stroke="#CCFF00" strokeWidth="0.08" strokeDasharray="0.6,0.9" opacity="0.4" />
          {/* Zero axis */}
          <line x1="0" y1={MID} x2="100" y2={MID} stroke="rgba(255,255,255,0.12)" strokeWidth="0.08" />

          {/* Bars up to cursor */}
          {RAW.map((v, i) => {
            if (i > block) return null;
            const x = i * bw + bw * 0.18;
            const w = bw * 0.64;
            const breach = Math.abs(v) > BAND;
            const c = clampBand(v);
            return (
              <g key={i}>
                {breach && (
                  <rect x={x} y={v > 0 ? yFor(v) : MID} width={w} height={Math.abs(v) * SCALE} fill="#FF6B35" opacity="0.38" rx="0.2" />
                )}
                <rect x={x} y={c > 0 ? yFor(c) : MID} width={w} height={Math.abs(c) * SCALE} fill={breach ? '#CCFF00' : 'rgba(204,255,0,0.5)'} rx="0.2" />
              </g>
            );
          })}

          {/* Sweep cursor */}
          <line x1={block * bw + bw / 2} y1="2" x2={block * bw + bw / 2} y2="54" stroke={mc} strokeWidth="0.07" opacity="0.4" />
        </svg>
        {/* HTML band labels (avoid stretched SVG text) */}
        <span className="dsm-band-label" style={{ top: `${(yFor(BAND) / 56) * 100}%` }}>+{BAND.toFixed(1)}%</span>
        <span className="dsm-band-label" style={{ top: `${(yFor(-BAND) / 56) * 100}%` }}>&minus;{BAND.toFixed(1)}%</span>
        </div>
        <div className="dsm-time-axis"><span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span></div>
      </div>

      {/* ── Legend ── */}
      <div className="dsm-legend">
        <span><i style={{ background: 'rgba(204,255,0,0.55)' }} />Settled deviation (corrected)</span>
        <span><i style={{ background: 'rgba(255,107,53,0.5)' }} />Uncorrected forecast error</span>
        <span><i className="dsm-legend-band" />&plusmn;1.0% DSM band</span>
      </div>

      {/* ── Stats ── */}
      <div className="dsm-stats" data-testid="dsm-stats">
        <div className="dsm-stat">
          <span className="dsm-stat-n" style={{ color: '#CCFF00' }}>100%</span>
          <span className="dsm-stat-l">Blocks settled in band</span>
        </div>
        <div className="dsm-stat">
          <span className="dsm-stat-n" style={{ color: '#FFB800' }}>{breaches}</span>
          <span className="dsm-stat-l">Breaches intercepted</span>
        </div>
        <div className="dsm-stat">
          <span className="dsm-stat-n">Rs {exposure.toLocaleString('en-IN')}</span>
          <span className="dsm-stat-l">DSM exposure neutralized</span>
        </div>
        <div className="dsm-stat">
          <span className="dsm-stat-n">{revisions}</span>
          <span className="dsm-stat-l">Revisions auto-filed</span>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="dsm-ft">
        <span>Settlement <strong className="lp-a">15-min blocks</strong></span>
        <span>Band <strong className="lp-a">&plusmn;1.0%</strong></span>
        <span>DSM charges MTD <strong className="lp-gold">Rs 0</strong></span>
        <span className="dsm-ft-brand"><span className="dsm-dot" style={{ background: mc, boxShadow: `0 0 5px ${mc}`, width: 4, height: 4 }} />PowerBESS.ai</span>
      </div>
    </div>
  );
}
